/**
 * Date and Timezone Utilities
 *
 * Provides timezone-aware date calculations for booking system
 */
/**
 * Get current date/time in a specific timezone
 * @param timezone IANA timezone string (e.g., 'Australia/Melbourne')
 * @returns Date object representing current time in that timezone
 */
export function getCurrentTimeInTimezone(timezone) {
    // Default to Melbourne if no timezone specified (safest - furthest ahead timezone)
    var tz = timezone || 'Australia/Melbourne';
    try {
        // Get current time as ISO string in the target timezone
        var formatter = new Intl.DateTimeFormat('en-US', {
            timeZone: tz,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false
        });
        var parts = formatter.formatToParts(new Date());
        var values_1 = {};
        parts.forEach(function (part) {
            if (part.type !== 'literal') {
                values_1[part.type] = part.value;
            }
        });
        // Create date in location's timezone
        return new Date(parseInt(values_1.year), parseInt(values_1.month) - 1, parseInt(values_1.day), parseInt(values_1.hour), parseInt(values_1.minute), parseInt(values_1.second));
    }
    catch (error) {
        console.error('Error getting time in timezone:', tz, error);
        // Fallback to Melbourne time if error
        return getCurrentTimeInTimezone('Australia/Melbourne');
    }
}
/**
 * Calculate the minimum available datetime based on lead time setting
 * @param timezone Location timezone
 * @param leadTimeSetting '2_hours' or 'next_day'
 * @returns Date object representing the minimum bookable time
 */
export function getMinimumBookableTime(timezone, leadTimeSetting) {
    if (leadTimeSetting === void 0) { leadTimeSetting = 'next_day'; }
    var now = getCurrentTimeInTimezone(timezone);
    if (leadTimeSetting === '2_hours') {
        // Add 2 hours to current time
        var minTime = new Date(now);
        minTime.setHours(minTime.getHours() + 2);
        return minTime;
    }
    else {
        // Next day - set to start of tomorrow
        var tomorrow = new Date(now);
        tomorrow.setDate(tomorrow.getDate() + 1);
        tomorrow.setHours(0, 0, 0, 0);
        return tomorrow;
    }
}
/**
 * Round time up to next slot interval
 * @param date Date to round
 * @param intervalMinutes Slot interval in minutes (e.g., 30, 60)
 * @returns Date rounded up to next interval
 */
export function roundUpToNextSlot(date, intervalMinutes) {
    var rounded = new Date(date);
    var minutes = rounded.getMinutes();
    var remainder = minutes % intervalMinutes;
    if (remainder !== 0) {
        rounded.setMinutes(minutes + (intervalMinutes - remainder));
    }
    rounded.setSeconds(0);
    rounded.setMilliseconds(0);
    return rounded;
}
/**
 * Check if a date should be shown in the date picker
 * @param date Date to check
 * @param timezone Location timezone
 * @param leadTimeSetting Lead time setting
 * @returns true if date should be available for selection
 */
export function isDateAvailableForBooking(date, timezone, leadTimeSetting) {
    if (leadTimeSetting === void 0) { leadTimeSetting = 'next_day'; }
    var minBookableTime = getMinimumBookableTime(timezone, leadTimeSetting);
    // For next_day, dates must be >= tomorrow
    if (leadTimeSetting === 'next_day') {
        var dateOnly = new Date(date);
        dateOnly.setHours(0, 0, 0, 0);
        var minDateOnly = new Date(minBookableTime);
        minDateOnly.setHours(0, 0, 0, 0);
        return dateOnly >= minDateOnly;
    }
    // For 2_hours, allow today if current time + 2 hours is still today
    if (leadTimeSetting === '2_hours') {
        var now = getCurrentTimeInTimezone(timezone);
        var today = new Date(now);
        today.setHours(0, 0, 0, 0);
        var dateOnly = new Date(date);
        dateOnly.setHours(0, 0, 0, 0);
        // Allow today and future dates
        return dateOnly >= today;
    }
    return false;
}
/**
 * Filter time slots based on lead time and current time
 * @param times Array of time strings (e.g., ['09:00', '10:00'])
 * @param selectedDate The date these times are for
 * @param timezone Location timezone
 * @param leadTimeSetting Lead time setting
 * @param intervalMinutes Slot interval
 * @returns Filtered array of available times
 */
export function filterAvailableTimes(times, selectedDate, timezone, leadTimeSetting, intervalMinutes) {
    if (leadTimeSetting === void 0) { leadTimeSetting = 'next_day'; }
    if (intervalMinutes === void 0) { intervalMinutes = 60; }
    if (leadTimeSetting !== '2_hours') {
        // For next_day, all times are available
        return times;
    }
    var now = getCurrentTimeInTimezone(timezone);
    var today = new Date(now);
    today.setHours(0, 0, 0, 0);
    var selectedDateOnly = new Date(selectedDate);
    selectedDateOnly.setHours(0, 0, 0, 0);
    // If selected date is not today, all times are available
    if (selectedDateOnly.getTime() !== today.getTime()) {
        return times;
    }
    // For today, filter out times that are less than 2 hours from now
    var minTime = new Date(now);
    minTime.setHours(minTime.getHours() + 2);
    var roundedMinTime = roundUpToNextSlot(minTime, intervalMinutes);
    return times.filter(function (timeStr) {
        var _a = timeStr.split(':').map(Number), hours = _a[0], minutes = _a[1];
        var slotTime = new Date(selectedDate);
        slotTime.setHours(hours, minutes, 0, 0);
        return slotTime >= roundedMinTime;
    });
}
/**
 * Validate if a time slot is still valid (at least 1 hour away)
 * @param selectedDate Selected date
 * @param selectedTime Selected time string (e.g., '14:00')
 * @param timezone Location timezone
 * @returns true if slot is at least 1 hour away
 */
export function validateTimeSlot(selectedDate, selectedTime, timezone) {
    var now = getCurrentTimeInTimezone(timezone);
    var _a = selectedTime.split(':').map(Number), hours = _a[0], minutes = _a[1];
    var slotDateTime = new Date(selectedDate);
    slotDateTime.setHours(hours, minutes, 0, 0);
    // Calculate difference in milliseconds
    var diffMs = slotDateTime.getTime() - now.getTime();
    var diffHours = diffMs / (1000 * 60 * 60);
    return diffHours >= 1;
}
